/**
 * Shared primitive types and Convex validators.
 * Import from this file to avoid re-declaring the same unions in multiple places.
 */
import { v } from "convex/values";

/** User role within the application. `none` is the default for newly-created users awaiting admin role assignment. */
export type Role = "none" | "student" | "teacher" | "admin";

/** Category for a musical piece. `lesson` pieces are placed in a lesson instead of a difficulty. */
export type PieceCategory = "song" | "exercise" | "lesson";

/** Language of a musical piece's lyrics / title. */
export type Language = "English" | "Cantonese" | "Mandarin" | "Japanese" | "Korean" | "Others";

/**
 * Lowest accuracy that still earns a passing grade (a D) — anything below is an
 * F. Lives here because both the client's grade table and the server's lesson
 * progress count read it; two copies would drift the moment D moves.
 */
export const PASSING_ACCURACY = 60;

/** Letter grade earned for a single completed run. S+ = top, F = below pass. */
export type Grade = "S+" | "S" | "A+" | "A" | "A-" | "B+" | "B" | "C+" | "C" | "D" | "F";

// Worst to best — index doubles as the rank used to compare two grades.
const GRADE_ORDER: Grade[] = ["F", "D", "C", "C+", "B", "B+", "A-", "A", "A+", "S", "S+"];

/**
 * Map an accuracy percentage (0-100) to a letter grade.
 *
 * `targetLineHidden` gates the S tiers: the target line is a visual timing
 * crutch, so S/S+ are only awarded when the run was played without it. With
 * the target line visible, 95%+ still tops out at A+ — the same ceiling as
 * before this tier existed.
 *
 * Lives here (not src/utils/grade.ts) so the highscore upsert mutation can
 * compare grades without duplicating the table — see pieceHighScores.ts.
 */
export function getGradeForAccuracy(accuracy: number, targetLineHidden = false): Grade {
  if (targetLineHidden) {
    if (accuracy >= 100) return "S+";
    if (accuracy >= 98) return "S";
  }
  if (accuracy >= 95) return "A+";
  if (accuracy >= 90) return "A";
  if (accuracy >= 85) return "B+";
  if (accuracy >= 80) return "B";
  if (accuracy >= 75) return "C+";
  if (accuracy >= 70) return "C";
  if (accuracy >= PASSING_ACCURACY) return "D";
  return "F";
}

/** Relative rank of a grade — higher is better. Use to compare two grades. */
export function gradeRank(grade: Grade): number {
  return GRADE_ORDER.indexOf(grade);
}

/** Convex validator for Role — use in mutation/query args. */
export const roleValidator = v.union(
  v.literal("none"),
  v.literal("student"),
  v.literal("teacher"),
  v.literal("admin")
);

/** Convex validator for PieceCategory. */
export const pieceCategoryValidator = v.union(
  v.literal("song"),
  v.literal("exercise"),
  v.literal("lesson"),
);

/** Convex validator for Language. */
export const languageValidator = v.union(
  v.literal("English"),
  v.literal("Cantonese"),
  v.literal("Mandarin"),
  v.literal("Japanese"),
  v.literal("Korean"),
  v.literal("Others")
);
